/**
 * R58-L-16 — pure math helpers for the Viewer's mouse-wheel zoom.
 *
 * Extracted from `Viewer.tsx` so the integration factor + cursor-centre
 * + clamp behaviour can be unit-tested without OpenSeadragon's real
 * viewport (jsdom doesn't simulate the OSD canvas math). The handler in
 * `Viewer.tsx` calls these helpers; behaviour is identical.
 *
 * Decision lock (Q9, 2026-05-23): "OSD smooth integration with 1.25×
 * per-100-px factor". One canonical mouse-wheel notch (deltaY = ±100,
 * mode 0) zooms by exactly 1.25× / (1/1.25)×. Trackpads emitting many
 * small deltas integrate smoothly via the exponent.
 */

export const ZOOM_STEP = 1.25;

/**
 * Normalise a WheelEvent-shaped delta to pixel-mode (mode 0). Browsers
 * may report `mode === 1` (lines) for classic wheel devices and
 * `mode === 2` (pages) for niche inputs; both get converted to a
 * consistent pixel delta so the exponent below behaves uniformly.
 */
export function normaliseWheelDeltaPx(
  deltaY: number,
  deltaMode: 0 | 1 | 2,
): number {
  const PX_PER_LINE = 16;
  const PX_PER_PAGE = 800;
  if (deltaMode === 1) return deltaY * PX_PER_LINE;
  if (deltaMode === 2) return deltaY * PX_PER_PAGE;
  return deltaY;
}

/**
 * Smooth-integration zoom factor for a single wheel event.
 *
 * - `deltaPx = -100` (one notch up)  → 1.25
 * - `deltaPx = +100` (one notch down)→ 0.8 (= 1 / 1.25)
 * - `deltaPx = 0`                     → 1.0 (no-op)
 * - `deltaPx = -50` (small trackpad delta) → 1.118... (sqrt of 1.25)
 *
 * Wheel-up is negative deltaY (so factor > 1 → zoom in).
 */
export function wheelZoomFactor(deltaPx: number): number {
  return Math.pow(ZOOM_STEP, -deltaPx / 100);
}

/**
 * Compute the **effective** zoom factor for one wheel event given the
 * current OSD zoom and the [minZ, maxZ] constraints. Returns `1` when
 * the wheel input should be a no-op (at floor zooming down, at ceiling
 * zooming up, or numerically negligible delta).
 *
 * Mirrors the contract:
 *   - AC9: wheel-down at fit-to-viewport is a no-op (returns 1).
 *   - AC10: wheel-up at max-zoom is a no-op (returns 1).
 *   - clamps to [minZ, maxZ] so a partial step doesn't overshoot.
 */
export function effectiveWheelZoomFactor(opts: {
  deltaY: number;
  deltaMode: 0 | 1 | 2;
  currentZoom: number;
  minZoom: number;
  maxZoom: number;
}): number {
  const deltaPx = normaliseWheelDeltaPx(opts.deltaY, opts.deltaMode);
  if (deltaPx === 0) return 1;
  const factor = wheelZoomFactor(deltaPx);
  const { currentZoom, minZoom, maxZoom } = opts;
  if (factor > 1 && currentZoom >= maxZoom - 1e-6) return 1;
  if (factor < 1 && currentZoom <= minZoom + 1e-6) return 1;
  const target = currentZoom * factor;
  const clamped = Math.min(maxZoom, Math.max(minZoom, target));
  const eff = clamped / currentZoom;
  if (Math.abs(eff - 1) < 1e-6) return 1;
  return eff;
}

/**
 * R58-L-16 AC1 — drag-rectangle longest-side fit.
 *
 * Given a rectangle in **image-pixel** coordinates and the viewport
 * dimensions in **image-pixel** coordinates (i.e. the size of the
 * fit-to-viewport view), returns the zoom multiple that makes the
 * rectangle's LONGEST side equal the corresponding viewport side. The
 * caller is responsible for capping the result at `maxZoom`.
 *
 * - Wider-than-tall rect → fit to viewport width (rect.w / viewportW).
 * - Taller-than-wide rect → fit to viewport height.
 *
 * Returns 1 when the rect is degenerate (zero area).
 */
export function longestSideZoomMultiple(opts: {
  rectWidth: number;
  rectHeight: number;
  viewportWidth: number;
  viewportHeight: number;
}): number {
  const { rectWidth, rectHeight, viewportWidth, viewportHeight } = opts;
  if (rectWidth <= 0 || rectHeight <= 0) return 1;
  if (viewportWidth <= 0 || viewportHeight <= 0) return 1;
  // Aspect ratio of the rect vs viewport tells us which side limits the
  // fit. If rect.w / rect.h > viewport.w / viewport.h, the rect is
  // wider-relative-to-viewport so width is the limiting axis (zoom =
  // viewportWidth / rectWidth, expressed as multiplier of fit-zoom 1.0).
  const rectAR = rectWidth / rectHeight;
  const viewportAR = viewportWidth / viewportHeight;
  if (rectAR > viewportAR) {
    return viewportWidth / rectWidth;
  }
  return viewportHeight / rectHeight;
}

/**
 * Compute where on screen an image-pixel will land after a zoom step.
 * The cursor-centred zoom contract (AC8) says the image pixel directly
 * under the cursor before the zoom must remain under the cursor after
 * the zoom. Given the BEFORE state, the cursor's image-pixel position
 * `(ipxX, ipxY)` and the new zoom factor, returns the new viewport
 * centre (in viewport coords) needed to keep that image pixel under
 * the cursor.
 *
 * In OpenSeadragon's `zoomBy(factor, refPoint)` signature, the
 * `refPoint` IS the point that stays fixed under the zoom, so this
 * helper exists to test the **reverse computation** (where would the
 * cursor be after a hypothetical pan-then-zoom path) in isolation —
 * the production code passes refPoint to OSD directly and relies on
 * its internal math.
 */
export function cursorCentredCheck(opts: {
  cursorViewportX: number;
  cursorViewportY: number;
  factor: number;
}): { stableX: number; stableY: number } {
  // The cursor position in viewport coords IS the stable point — that's
  // exactly what we hand to OSD as `refPoint`. The helper exists so the
  // unit test asserts the contract by name (rather than the test
  // restating it as "the same point we pass through").
  return {
    stableX: opts.cursorViewportX,
    stableY: opts.cursorViewportY,
  };
}
